import { BarChart2, TrendingUp, Calendar } from 'lucide-react';
import { type AnalyticsResponse } from '../../services/api/analytics';
import {
  CHANNEL_COLORS,
  CHANNEL_BG,
  CHANNEL_TEXT,
  CHANNEL_LABEL,
  type DailyTotal,
} from './analytics.constants';

interface Props {
  data: AnalyticsResponse | null;
  loading: boolean;
  dailyTotals: DailyTotal[];
  avgPerDay: number;
  topDay: DailyTotal | null;
  periodLabel: string;
  formatDate: (d: string) => string;
  channels: string[];
}

export const AnalyticsKpiCards = ({
  data,
  loading,
  dailyTotals,
  avgPerDay,
  topDay,
  periodLabel,
  formatDate,
  channels,
}: Props) => (
  <div className="space-y-4">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          <BarChart2 className="h-4 w-4" /> Totalt
        </div>
        <div className="text-3xl font-bold text-slate-900">
          {loading ? '\u2013' : (data?.totals.total ?? 0).toLocaleString('sv-SE')}
        </div>
        <div className="text-xs text-slate-500 mt-1">{periodLabel}</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          <TrendingUp className="h-4 w-4" /> Snitt per dag
        </div>
        <div className="text-3xl font-bold text-slate-900">{loading ? '\u2013' : avgPerDay}</div>
        <div className="text-xs text-slate-500 mt-1">Konversationer/dag</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          <Calendar className="h-4 w-4" /> Toppdag
        </div>
        <div className="text-3xl font-bold text-slate-900">
          {loading || !topDay ? '\u2013' : topDay.total}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {!loading && topDay ? formatDate(topDay.date) : '\u2013'}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          <BarChart2 className="h-4 w-4" /> Aktiva dagar
        </div>
        <div className="text-3xl font-bold text-slate-900">
          {loading ? '\u2013' : dailyTotals.filter((d) => d.total > 0).length}
        </div>
        <div className="text-xs text-slate-500 mt-1">Dagar med trafik</div>
      </div>
    </div>

    {!loading && channels.length > 0 && (
      <div
        className={`grid gap-4 ${
          channels.length === 1
            ? 'sm:grid-cols-1 max-w-xs'
            : channels.length === 2
              ? 'sm:grid-cols-2'
              : 'sm:grid-cols-3'
        }`}
      >
        {[...channels]
          .sort((a, b) => (data!.totals.byChannel[b] ?? 0) - (data!.totals.byChannel[a] ?? 0))
          .map((ch) => {
            const count = data?.totals.byChannel[ch] ?? 0;
            const pct =
              data && data.totals.total > 0 ? Math.round((count / data.totals.total) * 100) : 0;
            return (
              <div key={ch} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: CHANNEL_COLORS[ch] ?? '#94a3b8' }}
                  />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {CHANNEL_LABEL[ch] ?? ch}
                  </span>
                </div>
                <div className={`text-3xl font-bold ${CHANNEL_TEXT[ch] ?? 'text-slate-900'}`}>
                  {count.toLocaleString('sv-SE')}
                </div>
                <div className="text-xs text-slate-500 mt-1">{pct}% av total</div>
                <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${CHANNEL_BG[ch] ?? 'bg-slate-400'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
      </div>
    )}
  </div>
);
